import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, TrendingUp, Boxes, Users, GitMerge, Plug, ArrowRight } from "lucide-react";
import { api, fmtINR, SEV } from "../services/api";

function Metric({ icon: Icon, label, value, tone, sub }) {
  return (
    <div className="metric" style={tone ? { borderTopColor: tone } : {}}>
      <div className="metric-icon" style={tone ? { background: `${tone}14`, color: tone } : {}}><Icon size={18} /></div>
      <div className="metric-value">{value}</div>
      <div className="metric-label">{label}</div>
      {sub && <div className="metric-sub">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [sum, setSum] = useState(null);
  const [insights, setInsights] = useState([]);
  const [events, setEvents] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.dashboard().then(setSum);
    api.insights().then((d) => setInsights(d.insights));
    api.events().then((d) => setEvents(d.events.slice(0, 8)));
  }, []);

  if (!sum) return <div className="loading-page">Loading intelligence…</div>;

  const total = sum.total_insights || 1;

  return (
    <div className="dash">
      <div className="metrics-row">
        <Metric icon={AlertTriangle} label="Critical Insights" value={sum.critical_insights} tone={SEV.Critical.color} sub={`${sum.total_insights} correlated situations`} />
        <Metric icon={TrendingUp} label="Exposure Identified" value={fmtINR(sum.exposure_identified)} tone="#4f46e5" sub="open-order revenue at risk" />
        <Metric icon={Users} label="Dealers at Risk" value={sum.dealers_at_risk} tone="#0891b2" />
        <Metric icon={Boxes} label="SKUs at Risk" value={sum.skus_at_risk} tone="#7c3aed" />
        <Metric icon={GitMerge} label="Changes Correlated" value={sum.changes_correlated} tone="#059669" sub="cross-system signals" />
        <Metric icon={Plug} label="Systems Connected" value={sum.systems_connected} tone="#0f172a" />
      </div>

      <div className="sev-bar-card">
        <div className="card-h">Severity distribution</div>
        <div className="sev-bar">
          {["Critical", "High", "Medium", "Low"].map((s) => {
            const n = sum.by_severity[s] || 0;
            return n ? (
              <div key={s} className="sev-seg" style={{ flex: n, background: SEV[s].color }} title={`${s}: ${n}`}>
                {n}
              </div>
            ) : null;
          })}
        </div>
        <div className="sev-legend">
          {["Critical", "High", "Medium", "Low"].map((s) => (
            <span key={s} className="sev-leg"><i style={{ background: SEV[s].color }} />{s} · {sum.by_severity[s] || 0}</span>
          ))}
        </div>
      </div>

      <div className="dash-cols">
        <section className="panel">
          <div className="panel-h">
            <span>Needs Attention</span>
            <button className="link-btn" onClick={() => nav("/insights")}>View all <ArrowRight size={14} /></button>
          </div>
          <div className="attn-list">
            {insights.map((i) => (
              <button key={i.id} className="attn-item" onClick={() => nav(`/insights?open=${i.id}`)}>
                <span className="sev-chip" style={{ background: SEV[i.severity].bg, color: SEV[i.severity].color, borderColor: SEV[i.severity].ring }}>{i.severity}</span>
                <div className="attn-main">
                  <div className="attn-title">{i.title}</div>
                  <div className="attn-meta">{i.event_count} signals · {i.systems.length} systems · conf {i.confidence}%</div>
                </div>
                <div className="attn-exp">{fmtINR(i.exposure)}</div>
              </button>
            ))}
          </div>
        </section>

        <section className="panel">
          <div className="panel-h"><span>What Changed</span></div>
          <div className="change-list">
            {events.map((e) => (
              <div key={e.id} className="change-item">
                <span className={`kind-dot k-${e.kind}`} />
                <div>
                  <div className="change-what">{e.what}</div>
                  <div className="change-src">{e.source} · {e.time} · conf {e.confidence}%</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
